import { Box, Flex, useDisclosure, useToast } from "@chakra-ui/react";
import React, { useState, useRef, useEffect } from "react";
import {
  useParams,
  useHistory,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { useDispatch } from "react-redux";

// Import components
import LeftSidebar from "../components/V2/LeftSidebar";
import Scenes from "../components/V2/MainContent/Scenes";
import SceneDetails from "../components/V2/MainContent/SceneDetails";
import Header from "../components/V2/Header";

// Import API hooks
import {
  useGetSceneResultsQuery,
  useGenerateSceneMutation,
} from "services/api";
import { videosApi } from "services/api/v2";

export default function ProjectV2() {
  const { projectId } = useParams();
  const history = useHistory();
  const toast = useToast();
  const dispatch = useDispatch();

  // Sidebar width states with default values
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(250);

  // Refs for tracking resize state
  const isLeftResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startLeftWidthRef = useRef(0);

  // Form state for Generate Scene tab
  const [sceneForm, setSceneForm] = useState({
    title: "",
    prompt: "",
  });

  const [formErrors, setFormErrors] = useState({
    title: false,
    prompt: false,
  });

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [placeholderScenes, setPlaceholderScenes] = useState([]);

  // State for scene creation modal
  const {
    isOpen: isSceneModalOpen,
    onOpen: openSceneModal,
    onClose: closeSceneModal,
  } = useDisclosure();

  // API hooks
  const { data: sceneResults } = useGetSceneResultsQuery(projectId);
  const [generateScene] = useGenerateSceneMutation();

  // Extract scenes from API response - handle the actual backend structure
  const generatedScenes = sceneResults?.scene_results || [];

  // Combine generated scenes with placeholder scenes
  const allScenes = [...placeholderScenes, ...generatedScenes];

  // Left resize handlers
  const startLeftResize = (e) => {
    isLeftResizingRef.current = true;
    startXRef.current = e.clientX;
    startLeftWidthRef.current = leftSidebarWidth;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  // Handle resize movement
  const handleResize = (e) => {
    if (isLeftResizingRef.current) {
      const diff = e.clientX - startXRef.current;
      const newWidth = Math.max(
        100, // Minimum width of 100px
        Math.min(500, startLeftWidthRef.current + diff)
      );
      setLeftSidebarWidth(newWidth);
    }
  };

  // Stop resize
  const stopResize = () => {
    isLeftResizingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  // Add and remove event listeners
  useEffect(() => {
    document.addEventListener("mousemove", handleResize);
    document.addEventListener("mouseup", stopResize);

    return () => {
      document.removeEventListener("mousemove", handleResize);
      document.removeEventListener("mouseup", stopResize);
    };
  }, [leftSidebarWidth]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setSceneForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const handleGenerateSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = {
      title: !sceneForm.title.trim(),
      prompt: !sceneForm.prompt.trim(),
    };

    setFormErrors(errors);

    if (!errors.title && !errors.prompt) {
      // Start generation
      setIsGenerating(true);

      try {
        // Use the real API to generate scene
        const result = await generateScene({
          token: projectId,
          sceneData: {
            title: sceneForm.title,
            prompt: sceneForm.prompt,
          },
        }).unwrap();

        // Check if the API call was successful and we got a job ID
        if (result.success && result.captain_napalm_job_id) {
          // Add placeholder scene to the list
          const placeholderScene = {
            scene_name: result.scene_name,
            title: sceneForm.title,
            jobId: result.captain_napalm_job_id,
            isPlaceholder: true,
          };

          setPlaceholderScenes((prev) => [...prev, placeholderScene]);

          // Reset form
          setSceneForm({
            title: "",
            prompt: "",
          });

          // Close the creation modal
          closeSceneModal();

          // Show success toast
          toast({
            title: "Scene generation started",
            description: `"${sceneForm.title}" is now being generated.`,
            status: "info",
            duration: 5000,
            isClosable: true,
            position: "bottom-right",
          });
        } else {
          throw new Error("Failed to get job ID from API response");
        }
      } catch (error) {
        console.error("Generation failed:", error);

        // Show error toast
        toast({
          title: "Generation failed",
          description:
            "There was an error generating the scene. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-right",
        });
      } finally {
        setIsGenerating(false);
      }
    }
  };

  // Function to handle scene selection
  const handleSelectScene = (sceneId) => {
    history.push(`/v2/project/${projectId}/scene/${sceneId}`);
  };

  // Function to handle scene completion
  const handleSceneComplete = (sceneId) => {
    // Remove the placeholder scene
    setPlaceholderScenes((prev) =>
      prev.filter((scene) => scene.scene_name !== sceneId)
    );

    // Invalidate the Videos tag to refetch video data
    dispatch(videosApi.util.invalidateTags(["Videos"]));

    // Show completion toast
    toast({
      title: "Scene generation completed",
      description: "Scene has been generated and video conversion started!",
      status: "success",
      duration: 5000,
      isClosable: true,
      position: "bottom-right",
    });
  };

  // Effect to check for completed placeholder scenes and refetch scenes
  useEffect(() => {
    const checkCompletedScenes = async () => {
      const completedScenes = [];

      for (const placeholder of placeholderScenes) {
        try {
          // Check if the scene now exists in the generated scenes
          const exists = generatedScenes.some(
            (scene) => scene.scene_name === placeholder.scene_name
          );
          if (exists) {
            completedScenes.push(placeholder.scene_name);
          }
        } catch (error) {
          console.error("Error checking scene completion:", error);
        }
      }

      // Remove completed placeholder scenes
      if (completedScenes.length > 0) {
        setPlaceholderScenes((prev) =>
          prev.filter((scene) => !completedScenes.includes(scene.scene_name))
        );

        // Show completion toast
        toast({
          title: "Scene generation completed",
          description: `${completedScenes.length} scene${
            completedScenes.length > 1 ? "s" : ""
          } have been successfully generated!`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "bottom-right",
        });
      }
    };

    // Check every 5 seconds
    const interval = setInterval(checkCompletedScenes, 5000);
    return () => clearInterval(interval);
  }, [placeholderScenes, generatedScenes, toast]);

  const handleSaveDraft = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = {
      title: !sceneForm.title.trim(),
      prompt: !sceneForm.prompt.trim(),
    };

    setFormErrors(errors);

    if (!errors.title && !errors.prompt) {
      try {
        // Use the real API to create a draft scene
        await generateScene({
          token: projectId,
          sceneData: {
            title: sceneForm.title,
            prompt: sceneForm.prompt,
            status: "draft", // Set status as draft
          },
        }).unwrap();

        // Reset form
        setSceneForm({
          title: "",
          prompt: "",
        });

        // Close the modal
        closeSceneModal();

        // Show success toast
        toast({
          title: "Draft saved",
          description: `"${sceneForm.title}" has been saved as a draft.`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "bottom-right",
        });
      } catch (error) {
        console.error("Draft save failed:", error);

        // Show error toast
        toast({
          title: "Save failed",
          description: "There was an error saving the draft. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-right",
        });
      }
    }
  };

  return (
    <Box height="100vh" overflow="hidden" width="100%">
      {/* Header */}
      <Header generatedScenes={allScenes} projectId={projectId} />

      {/* Main Container - below navbar */}
      <Flex height="calc(100vh - 60px)" marginTop="60px" width="100%">
        {/* Left Sidebar */}
        <LeftSidebar
          leftSidebarWidth={leftSidebarWidth}
          startLeftResize={startLeftResize}
        />

        {/* Main Content */}
        <Box flex="1" height="100%" overflowY="auto" p={6}>
          <Switch>
            <Route
              path="/v2/project/:projectId/scene/:sceneId"
              render={() => <SceneDetails />}
            />
            <Route
              path="/v2/project/:projectId"
              render={() => (
                <Scenes
                  formErrors={formErrors}
                  generatedScenes={allScenes}
                  handleFormChange={handleFormChange}
                  handleGenerateSubmit={handleGenerateSubmit}
                  handleSaveDraft={handleSaveDraft}
                  handleSceneComplete={handleSceneComplete}
                  handleSelectScene={handleSelectScene}
                  isGenerating={isGenerating}
                  isModalOpen={isSceneModalOpen}
                  onModalClose={closeSceneModal}
                  onModalOpen={openSceneModal}
                  projectId={projectId}
                  sceneForm={sceneForm}
                />
              )}
            />
            <Route
              path="*"
              render={() => <Redirect to={`/v2/project/${projectId}`} />}
            />
          </Switch>
        </Box>
      </Flex>
    </Box>
  );
}
